/**
 * One group of results: its label, its exact matches, then its related ones (§5a).
 *
 * The related hits sit under a quiet "Related" divider, below everything the words found and
 * never mixed into it. While they may still arrive, the group draws **exactly one** skeleton row
 * in the place they would land — the "layout known, data not yet here" rule (CLAUDE.md, loading
 * states) — and no spinner. The row goes when the stream says `done` or `semantic_unavailable`.
 */

import type { ReactNode } from 'react';
import { Fragment, useId } from 'react';

import { Eyebrow, Skeleton } from '@/components';
import { useMessages } from '@/lib/i18n';

import type { SearchGroupKey, SearchHit } from './api';
import { hitKey } from './model';
import styles from './Search.module.css';

/** The catalog key of each group's label. */
export const GROUP_LABEL = {
  drawing: 'group.drawing',
  task: 'group.task',
  comment: 'group.comment',
  node: 'group.node',
  project: 'group.project',
  person: 'group.person',
} as const satisfies Readonly<Record<SearchGroupKey, string>>;

export interface SearchGroupProps {
  readonly group: SearchGroupKey;
  readonly lexical: readonly SearchHit[];
  readonly semantic: readonly SearchHit[];
  readonly pending: boolean;
  /** Draws one hit; the overlay and the results page draw rows differently. */
  readonly renderHit: (hit: SearchHit) => ReactNode;
  /** "Show all" in the overlay, "Load more" on the results page. */
  readonly action?: ReactNode;
  /** `listbox` inside the overlay, where the rows are options. */
  readonly role?: 'group' | undefined;
}

export function SearchGroup({
  group,
  lexical,
  semantic,
  pending,
  renderHit,
  action,
  role,
}: SearchGroupProps) {
  const t = useMessages('search');
  const labelId = useId();

  return (
    <section className={styles.group} role={role} aria-labelledby={labelId}>
      <div className={styles.groupHead}>
        <span id={labelId}>
          <Eyebrow>{t(GROUP_LABEL[group])}</Eyebrow>
        </span>
        {action}
      </div>
      {lexical.map((hit) => (
        <Fragment key={hitKey(hit)}>{renderHit(hit)}</Fragment>
      ))}
      {semantic.length > 0 ? (
        <>
          <div className={styles.related} role="presentation">
            {t('group.related')}
          </div>
          {semantic.map((hit) => (
            <Fragment key={hitKey(hit)}>{renderHit(hit)}</Fragment>
          ))}
        </>
      ) : null}
      {pending ? (
        <div
          className={styles.pendingRow}
          role="status"
          aria-busy="true"
          aria-label={t('group.pending')}
        >
          <Skeleton width={16} height={16} />
          <Skeleton width="48%" height={12} />
        </div>
      ) : null}
    </section>
  );
}
